'use client';
import React, { createContext, useState } from 'react';
import {
	AddressInformationType,
	AssetsType,
	CaseInformationType,
	ContactInformationType,
	DebtsType,
	DeterminationType,
	EmploymentIncomeType,
	ExpensesType,
	FinancialStatusFormSubmissionSchema,
	FinancialStatusFormSubmissionType,
	JsonData,
	SignatureType,
	ProgressSteps,
} from '../types';
import dataJson from '../../data/data.json'; // Import JSON data
import progressStepsJson from '../../data/progress.json';
import {
	FinancialStatusProviderProps,
	FinancialStatusContextProps,
	defaultContextProps,
	PlaceholderFunctionProps,
} from './context';

const jsonData = dataJson as JsonData; // Type the data
const progressSteps = progressStepsJson as ProgressSteps;

export const FinancialStatusContext = createContext<FinancialStatusContextProps>(defaultContextProps);

export function FinancialStatusProvider({ children }: FinancialStatusProviderProps) {
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [language, setLanguage] = useState<'en' | 'es'>('en');
	const [validatedSteps, setValidatedSteps] = useState<string[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(defaultContextProps.isSubmitting);
	const [hasSubmissionError, setHasSubmissionError] = useState<boolean>(defaultContextProps.hasSubmissionError);
	const [pdfHref, setPdfHref] = useState<string | undefined>(undefined);
	const [caseInformation, setCaseInformation] = useState<CaseInformationType>({
		defendantName: '',
		caseNumber: '',
		judge: '',
	});

	const [contactInformation, setContactInformation] = useState<ContactInformationType>({
		firstName: '',
		lastName: '',
		middleName: '',
		DOB: '',
		phone: '',
		email: '',
	});

	const [addressInformation, setAddressInformation] = useState<AddressInformationType>({
		mailingAddress: '',
		apartmentNumber: '',
		city: '',
		state: 'Utah',
		zipCode: '',
	});

	const [determination, setDetermination] = useState<DeterminationType>({
		maritalStatus: 'Single',
		hasDependents: false,
		dependents: [],
	});

	const [employmentIncome, setEmploymentIncome] = useState<EmploymentIncomeType>({
		employment: {
			employmentStatus: 'Unemployed',
			employmentType: undefined,
			employerName: '',
			hourlyRate: undefined,
			hoursWorkedWeekly: undefined,
			spouseEmploymentStatus: 'No',
			spouseEmploymentType: undefined,
			spouseEmployerName: '',
			spouseHourlyRate: undefined,
			spouseHoursWorkedWeekly: undefined,
		},
		income: {
			retirement: 0,
			unemployment: 0,
			alimony: 0,
			childSupport: 0,
			ssiDisability: 0,
			publicAssistance: 0,
			otherIncome: 0,
		},
		userEnteredIncome: 0,
		explanation: '',
	});

	const [assets, setAssets] = useState<AssetsType>({
		cashValue: 0,
		propertyValue: 0,
		vehicleValue: 0,
		makeAndModel: '',
		retirementValue: 0,
		stockValue: 0,
		otherItemsValue: 0,
		totalAssets: 0,
	});

	const [debts, setDebts] = useState<DebtsType>({
		hasDebts: false,
		debts: [],
		totalAmountOwed: 0,
	});

	const [expenses, setExpenses] = useState<ExpensesType>({
		housing: 0,
		food: 0,
		utilities: 0,
		transportation: 0,
		medical: 0,
		school: 0,
		clothing: 0,
		childSupport: 0,
		alimony: 0,
		other: 0,
		attorneyFees: 0,
		necessities: 0,
		totalExpenses: 0,
	});

	//eslint-disable-next-line
	const [signature, setSignature] = useState<SignatureType>({
		userExplanation: '',
		locationSignedAt: '',
		dateSigned: new Date().toISOString(), // Set to ISO 8601 format
		defendantSignature: '',
	});

	const getPlaceholder = ({ step, value }: PlaceholderFunctionProps) => {
		const stepData = jsonData[step];

		if (stepData.questions) {
			const question = stepData.questions.find((q) => q.key === value);
			if (question?.placeholder) {
				return question.placeholder[language];
			}
		}

		return '';
	};

	const updateCurrentStep = (step: number) => {
		setCurrentStep(step);
	};

	const updateCaseInformation = (data: CaseInformationType) => {
		setCaseInformation(data);
		setValidatedSteps([...validatedSteps, 'Case Information']);
	};

	const updateContactInformation = (data: ContactInformationType) => {
		setContactInformation(data);
		setValidatedSteps([...validatedSteps, 'Contact Information']);
	};

	const updateAddressInformation = (data: AddressInformationType) => {
		setAddressInformation(data);
		setValidatedSteps([...validatedSteps, 'Address Information']);
	};

	const updateDetermination = (data: DeterminationType) => {
		setDetermination(data);
		setValidatedSteps([...validatedSteps, 'Determination']);
	};

	const updateEmploymentIncomeData = (data: EmploymentIncomeType) => {
		setEmploymentIncome(data);
		setValidatedSteps([...validatedSteps, 'Employment / Income']);
	};

	const updateAssets = (data: AssetsType) => {
		setAssets(data);
		setValidatedSteps([...validatedSteps, 'Assets']);
	};

	const updateDebts = (data: DebtsType) => {
		setDebts(data);
		setValidatedSteps([...validatedSteps, 'Debts']);
	};

	const updateExpenses = (data: ExpensesType) => {
		setExpenses(data);
		setValidatedSteps([...validatedSteps, 'Expenses']);
	};

	const updateSignature = (data: SignatureType) => {
		setSignature(data);
		setValidatedSteps([...validatedSteps, 'Signature']);
	};

	const submitForm = async (data: SignatureType) => {
		updateSignature(data);
		const obj: FinancialStatusFormSubmissionType = {
			lang: language,
			caseInformation,
			contactInformation,
			addressInformation,
			determination,
			employmentIncome,
			assets,
			debts,
			expenses,
			signature: data,
		};
		try {
			FinancialStatusFormSubmissionSchema.parse(obj);
			setIsSubmitting(true);
			setHasSubmissionError(false);
			const res = await fetch('/api/forms/financial-status', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(obj),
			});
			const pdfURL = window.URL.createObjectURL(await res.blob());
			setPdfHref(pdfURL);
			setIsSubmitting(false);
		} catch (error) {
			console.error('Error submitting form', error);
			setIsSubmitting(false);
			setHasSubmissionError(true);
		}
	};

	return (
		<FinancialStatusContext.Provider
			value={{
				language,
				setLanguage,
				currentStep,
				getPlaceholder,
				updateCurrentStep,
				validatedSteps,
				jsonData, // Provide the JSON data
				progressSteps, // JSON for the steps
				updateCaseInformation,
				updateContactInformation,
				updateAddressInformation,
				updateDetermination,
				updateEmploymentIncomeData,
				updateAssets,
				updateDebts,
				updateExpenses,
				updateSignature,
				submitForm,
				isSubmitting,
				hasSubmissionError,
				pdfHref,
			}}
		>
			{children}
		</FinancialStatusContext.Provider>
	);
}
